import { TituloOrdenable } from '@/components/dominio/TituloOrdenable';
import { FiltroPeriodosSemanal } from '@/components/filtros/FiltroPeriodosSemanal';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAvanceMatriz,
  useAvanceMatrizSemanal,
  useAvancePartidas,
  usePeriodosSemanales,
} from '@/hooks/useAvanceMatriz';
import { ESTADOS_VENTA, colorArgbEstado, colorEstado, colorFondoEstado } from '@/lib/coloresEstado';
import {
  type ColumnaHoja,
  construirArbolColumnas,
  etiquetaSemana,
  keyMes,
  keySemana,
} from '@/lib/columnasArbol';
import { type FilaExport, exportarMatrizExcel } from '@/lib/exportarExcel';
import { type OrdenColumna, ordenarHermanos, siguienteOrden } from '@/lib/ordenColumna';
import { CaretDown, CaretRight, CircleNotch, DownloadSimple } from '@phosphor-icons/react';
import { type ReactElement, useMemo, useState } from 'react';

const NOMBRES_MES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

type Metrica = 'pct' | 'monto' | 'm2';
type Modo = 'acum' | 'mes';

/** Color de fondo según rango de avance acumulado (heatmap rojo→verde). */
function colorAvance(pct: number | null): string {
  if (pct == null || Number.isNaN(pct)) return '';
  if (pct < 0) return 'bg-red-100 text-red-900';
  if (pct === 0) return '';
  if (pct < 0.25) return 'bg-red-50 text-red-900';
  if (pct < 0.5) return 'bg-orange-50 text-orange-900';
  if (pct < 0.75) return 'bg-yellow-50 text-yellow-900';
  if (pct < 1) return 'bg-lime-50 text-lime-900';
  return 'bg-emerald-100 text-emerald-900 font-medium';
}

function fmtPct(v: number | null): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toLocaleString('es-CR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;
}
function fmtMonto(v: number | null): string {
  if (v == null || Number.isNaN(v) || v === 0) return '—';
  const neg = v < 0;
  return `${neg ? '-' : ''}₡${Math.round(Math.abs(v))
    .toLocaleString('es-CR', { maximumFractionDigits: 0 })
    .replace(/,/g, ' ')}`;
}
function fmtM2(v: number | null): string {
  if (v == null || Number.isNaN(v) || v === 0) return '—';
  return v.toLocaleString('es-CR', { maximumFractionDigits: 1 });
}

/** Celda agregada por columna. Roll-up correcto a nivel grupo:
 *  - % avance = Σ(pct_obra × costo_casa_obra) / Σ costo_casa  → ponderado por
 *    COSTO directo (= Power BI). El numerador y el denominador son COSTO.
 *  - Monto (₡) = Σ(avance × COSTO) — costo teórico construido (coherente con % y m²).
 *  - m² = Σ(pct × área).
 *  - Del mes: monto/m²/% del mes vienen DIRECTO del backend (no delta del
 *    acumulado clipado), así un reporte sobre una partida ya al 100% sigue
 *    contando. El % del mes se pondera por costo igual que el acumulado. */
interface Celda {
  pct: number | null; // % avance acumulado (ponderado por costo)
  numCostoAcum: number; // numerador costo acum = Σ(pct_obra × costo_casa_obra)
  costoCasa: number; // Σ costo directo total de las obras (denominador %)
  montoAcum: number; // monto de costo avanzado acum (avance × costo)
  m2Acum: number; // m² construidos acum (Σ pct × area)
  // --- del mes (directos del backend, aditivos entre obras) ---
  numCostoMes: number; // numerador costo del mes (Σ pct_mes × costo_casa)
  montoMes: number; // monto producido del mes (avance_mes × venta)
  m2Mes: number; // m² del mes (pct_avance_mes × area)
  estado?: string | null; // estado de venta del mes (solo nivel obra, para color)
}
const celdaVacia = (): Celda => ({
  pct: null,
  numCostoAcum: 0,
  costoCasa: 0,
  montoAcum: 0,
  m2Acum: 0,
  numCostoMes: 0,
  montoMes: 0,
  m2Mes: 0,
});
function acumular(c: Celda, datos: Omit<Celda, 'pct'>): Celda {
  const next: Celda = {
    pct: c.pct,
    numCostoAcum: c.numCostoAcum + datos.numCostoAcum,
    costoCasa: c.costoCasa + datos.costoCasa,
    montoAcum: c.montoAcum + datos.montoAcum,
    m2Acum: c.m2Acum + datos.m2Acum,
    numCostoMes: c.numCostoMes + datos.numCostoMes,
    montoMes: c.montoMes + datos.montoMes,
    m2Mes: c.m2Mes + datos.m2Mes,
  };
  next.pct = next.costoCasa > 0 ? next.numCostoAcum / next.costoCasa : null;
  return next;
}

interface NodoObra {
  tipo: 'obra';
  worksNo: string;
  display: string | null;
  porColumna: Map<string, Celda>;
}
interface NodoBloque {
  tipo: 'bloque';
  bloque: string;
  key: string;
  porColumna: Map<string, Celda>;
  obras: NodoObra[];
}
interface NodoProyecto {
  tipo: 'proyecto';
  proyecto: string;
  porColumna: Map<string, Celda>;
  bloques: NodoBloque[];
}
// Las columnas son hojas del árbol Año › Mes › Semana (mes o semana).
type ColDef = ColumnaHoja;

/** Filas de partidas (drill-down) de una obra expandida. */
function FilasPartidasAvance({
  worksNo,
  cols,
  metrica,
  modo,
}: {
  worksNo: string;
  cols: ColDef[];
  metrica: Metrica;
  modo: Modo;
}): ReactElement {
  const { data, isLoading } = useAvancePartidas(worksNo);
  if (isLoading) {
    return (
      <TableRow>
        <TableCell
          colSpan={cols.length + 1}
          className="bg-zinc-50/50 text-xs text-muted-foreground"
        >
          <div className="flex items-center gap-2 pl-14">
            <CircleNotch className="h-3 w-3 animate-spin" weight="bold" /> Cargando partidas…
          </div>
        </TableCell>
      </TableRow>
    );
  }
  const filas = data?.data ?? [];
  // Por partida → { colKey: {pct, monto} }. (m² por partida no aplica: el área es de la obra.)
  const porPartida = new Map<
    string,
    { display: string | null; cel: Map<string, { pct: number; monto: number }> }
  >();
  for (const f of filas) {
    let p = porPartida.get(f.taskNo);
    if (!p) {
      p = { display: f.tareaDisplay, cel: new Map() };
      porPartida.set(f.taskNo, p);
    }
    p.cel.set(`${f.anio}-${f.mes}`, { pct: f.pctAvance, monto: f.montoAvance });
  }
  const partidas = [...porPartida.entries()]
    .filter(([, v]) => [...v.cel.values()].some((c) => c.pct > 0))
    .sort((a, b) => a[0].localeCompare(b[0], 'es', { numeric: true }));

  const valorPartida = (cel: Map<string, { pct: number; monto: number }>, idx: number): string => {
    // Las partidas solo tienen datos MENSUALES; en columnas de semana no aplica.
    if (cols[idx]?.semanaIso != null) return '·';
    const cur = cel.get(cols[idx]?.key ?? '');
    if (metrica === 'pct') {
      if (modo === 'acum') return cur ? fmtPct(cur.pct) : '—';
      const a = cur?.pct ?? carryPart(cel, cols, idx, 'pct');
      const b = carryPart(cel, cols, idx - 1, 'pct');
      return cur ? fmtPct(a - b) : '—';
    }
    // monto (m² no aplica a partida)
    if (modo === 'acum') return cur ? fmtMonto(cur.monto) : '—';
    const a = carryPart(cel, cols, idx, 'monto');
    const b = carryPart(cel, cols, idx - 1, 'monto');
    return fmtMonto(a - b);
  };

  return (
    <>
      {partidas.map(([taskNo, v]) => (
        <TableRow key={`PT|${worksNo}|${taskNo}`} className="bg-zinc-50/50">
          <TableCell className="sticky left-0 z-10 bg-zinc-50 text-xs">
            <div className="pl-14">
              <span className="font-medium">{taskNo}</span>
              {v.display && (
                <span className="ml-1 text-[10px] text-muted-foreground">
                  {v.display.replace(new RegExp(`^${taskNo}\\s*-?\\s*`), '')}
                </span>
              )}
            </div>
          </TableCell>
          {cols.map((c, idx) => {
            const cur = v.cel.get(c.key);
            const borderLeft =
              idx === 0 || cols[idx - 1]?.anio !== c.anio ? 'border-l border-zinc-200' : '';
            return (
              <TableCell
                key={c.key}
                className={`text-center text-xs tabular-nums ${colorAvance(cur?.pct ?? null)} ${borderLeft}`}
              >
                {valorPartida(v.cel, idx)}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </>
  );
}
function carryPart(
  cel: Map<string, { pct: number; monto: number }>,
  cols: ColDef[],
  idx: number,
  campo: 'pct' | 'monto',
): number {
  for (let i = idx; i >= 0; i--) {
    // Solo columnas de MES (las partidas no tienen datos semanales).
    if (cols[i]?.semanaIso != null) continue;
    const c = cel.get(cols[i]?.key ?? '');
    if (c) return c[campo];
  }
  return 0;
}

export function AvanceMatrizPantalla() {
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  // El orden se guarda por CLAVE de columna (no por posición): al cambiar de vista
  // mes/semana, de filtros o de métrica, un orden huérfano simplemente se ignora.
  const [ordenCol, setOrdenCol] = useState<{ key: string; dir: 'desc' | 'asc' } | null>(null);
  const [metrica, setMetrica] = useState<Metrica>('pct');
  const [modo, setModo] = useState<Modo>('acum');
  // Vista: 'mes' (resumen) o 'semana' (desglose ISO).
  const [vista, setVista] = useState<'mes' | 'semana'>('mes');
  // Semanas ISO marcadas (claves "anio-mes-semana"). Solo en vista semana.
  const [semanasSelRaw, setSemanasSelRaw] = useState<string[]>([]);
  // Colorear el fondo según el estado de venta de cada obra/mes.
  const [colorearEstado, setColorearEstado] = useState(false);
  // Desglosar el Gran Total en sub-filas por estado de venta.
  const [totalPorEstadoAbierto, setTotalPorEstadoAbierto] = useState(false);
  // En vista 'mes' no se pasan semanas (el fetch semanal queda inactivo).
  const semanasSel = vista === 'semana' ? semanasSelRaw : [];

  const { data: periodosSem } = usePeriodosSemanales();
  const { data, isLoading, error } = useAvanceMatriz();
  const { data: dataSem, isLoading: cargandoSem } = useAvanceMatrizSemanal(semanasSel);

  const matriz = useMemo(() => {
    const filasMensual = data?.data ?? [];
    const filasSem = dataSem?.data ?? [];
    const modoSemanal = semanasSel.length > 0;
    // En modo semanal el selector propio es el ÚNICO control de tiempo: las filas
    // (y por ende los meses) salen SOLO del dataset semanal, ignorando el filtro
    // global. Sin semanas marcadas → vista mensual normal (respeta filtro global).
    const filas = modoSemanal ? filasSem : filasMensual;

    // Meses a expandir = los que tienen semanas en el dataset semanal (= las que
    // el usuario marcó en el selector propio). Semanas disponibles por mes.
    const semanasPorMes = new Map<string, number[]>();
    const mesesExpandidos = new Set<string>();
    for (const f of filasSem) {
      if (f.semanaIso == null) continue;
      const mk = keyMes(f.anio, f.mes);
      mesesExpandidos.add(mk);
      const arr = semanasPorMes.get(mk) ?? [];
      if (!arr.includes(f.semanaIso)) arr.push(f.semanaIso);
      semanasPorMes.set(mk, arr);
    }

    // Meses base: en modo semanal, solo los meses de las semanas marcadas; en
    // mensual, los del dataset mensual.
    const arbol = construirArbolColumnas(
      filas.map((f) => ({ anio: f.anio, mes: f.mes })),
      semanasPorMes,
      mesesExpandidos,
    );
    const cols: ColDef[] = arbol.hojas;

    const proyectos = new Map<string, NodoProyecto>();
    const bloques = new Map<string, NodoBloque>();
    const obras = new Map<string, NodoObra>();

    // Inserta una fila (mensual o semanal) en la columna `colKey` indicada.
    // Para semanas, el acumulado (pct/numCostoAcum/montoAcum/m2Acum) va en 0 — la
    // tabla semanal solo trae "del período"; el modo Acumulado en columnas de
    // semana muestra "—".
    const insertar = (f: (typeof filas)[number], colKey: string, esSemana: boolean) => {
      const proy = f.bloque ?? '(sin proyecto)';
      const bloque = f.subBloque ?? '(sin bloque)';
      const bloqueKey = `${proy}|${bloque}`;
      const m2 = f.pctAvance * f.areaM2Total;

      let nodoP = proyectos.get(proy);
      if (!nodoP) {
        nodoP = { tipo: 'proyecto', proyecto: proy, porColumna: new Map(), bloques: [] };
        proyectos.set(proy, nodoP);
      }
      let nodoB = bloques.get(bloqueKey);
      if (!nodoB) {
        nodoB = { tipo: 'bloque', bloque, key: bloqueKey, porColumna: new Map(), obras: [] };
        bloques.set(bloqueKey, nodoB);
        nodoP.bloques.push(nodoB);
      }
      let nodoO = obras.get(f.worksNo);
      if (!nodoO) {
        nodoO = {
          tipo: 'obra',
          worksNo: f.worksNo,
          display: f.proyectoDisplay,
          porColumna: new Map(),
        };
        obras.set(f.worksNo, nodoO);
        nodoB.obras.push(nodoO);
      }
      const costoCasa = f.sumPresupuestoVenta; // mal nombrado en el schema, es costo
      const datos = {
        // Acumulado: 0 para semanas (no existe acumulado semanal).
        numCostoAcum: esSemana ? 0 : f.pctAvance * costoCasa,
        costoCasa,
        montoAcum: esSemana ? 0 : f.sumOutstanding,
        m2Acum: esSemana ? 0 : m2,
        // Del período: para semanas viene del backend semanal (sumValorGanadoMes
        // = valor de la semana, pctAvanceMes = % de la semana).
        numCostoMes: f.pctAvanceMes * costoCasa,
        montoMes: f.sumValorGanadoMes,
        m2Mes: f.pctAvanceMes * f.areaM2Total,
      };
      // El estado de venta solo se guarda a nivel OBRA (un grupo mezcla obras
      // de distintos estados); no se propaga a bloque/proyecto.
      nodoO.porColumna.set(colKey, {
        pct: esSemana ? null : f.pctAvance,
        ...datos,
        estado: f.estadoVenta ?? null,
      });
      nodoB.porColumna.set(colKey, acumular(nodoB.porColumna.get(colKey) ?? celdaVacia(), datos));
      nodoP.porColumna.set(colKey, acumular(nodoP.porColumna.get(colKey) ?? celdaVacia(), datos));
    };

    if (modoSemanal) {
      // Solo columnas de semana (el dataset semanal es la única fuente de tiempo).
      for (const f of filasSem) {
        if (f.semanaIso == null) continue;
        insertar(f, keySemana(f.anio, f.mes, f.semanaIso), true);
      }
    } else {
      // Vista mensual normal: una celda de mes por fila mensual.
      for (const f of filas) insertar(f, keyMes(f.anio, f.mes), false);
    }

    const proyectosArr = [...proyectos.values()].sort((a, b) =>
      a.proyecto.localeCompare(b.proyecto),
    );
    for (const p of proyectosArr) {
      p.bloques.sort((a, b) => a.bloque.localeCompare(b.bloque));
      for (const b of p.bloques) b.obras.sort((a, b) => a.worksNo.localeCompare(b.worksNo));
    }

    const total = new Map<string, Celda>();
    for (const p of proyectosArr)
      for (const [k, c] of p.porColumna) total.set(k, acumular(total.get(k) ?? celdaVacia(), c));

    // Subtotal por estado de venta (solo nivel obra, que es donde vive el estado).
    // Acumula Celdas completas para que el % ponderado y monto/m² se deriven igual
    // que en el resto. estado → (colKey → Celda).
    const totalPorEstado = new Map<string, Map<string, Celda>>();
    for (const p of proyectosArr)
      for (const b of p.bloques)
        for (const o of b.obras)
          for (const [k, c] of o.porColumna) {
            if (!c.estado) continue;
            let m = totalPorEstado.get(c.estado);
            if (!m) {
              m = new Map();
              totalPorEstado.set(c.estado, m);
            }
            m.set(k, acumular(m.get(k) ?? celdaVacia(), c));
          }

    return { cols, arbol, proyectos: proyectosArr, total, totalPorEstado };
  }, [data, dataSem, semanasSel]);

  const toggle = (key: string) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const todasLasKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const p of matriz.proyectos) {
      keys.add(`P|${p.proyecto}`);
      for (const b of p.bloques) keys.add(`B|${b.key}`);
    }
    return keys;
  }, [matriz]);

  if (isLoading || (semanasSel.length > 0 && cargandoSem)) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
      </div>
    );
  }
  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }
  if (matriz.proyectos.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Sin datos de avance con los filtros seleccionados.
          </p>
        </CardContent>
      </Card>
    );
  }

  const hojas = matriz.cols;
  const iniciaAnio = (idx: number) => idx === 0 || hojas[idx - 1]?.anio !== hojas[idx]?.anio;

  // conColor: las filas de OBRA pintan el FONDO completo de la celda según el
  // estado de venta del mes (cuando "Color estado" está activo). En ese modo se
  // reemplaza el heatmap de avance por el color de estado para que se vea limpio.
  const renderCeldas = (porColumna: Map<string, Celda>, conColor = false) =>
    hojas.map((c, idx) => {
      const borderLeft = iniciaAnio(idx) ? 'border-l border-zinc-200' : '';
      const cur = porColumna.get(c.key);
      const esSemana = c.semanaIso != null;
      const bgEstado = conColor && colorearEstado ? colorFondoEstado(cur?.estado) : '';
      // Heatmap: % acumulado del mes; en columnas de semana no hay acumulado.
      const pctAcum = esSemana ? null : (cur?.pct ?? null);
      let texto: string;
      if (modo === 'acum') {
        // En modo Acumulado, una columna de semana no tiene acumulado → "—".
        texto = esSemana
          ? '—'
          : metrica === 'pct'
            ? fmtPct(pctAcum)
            : metrica === 'monto'
              ? fmtMonto(cur?.montoAcum ?? null)
              : fmtM2(cur?.m2Acum ?? null);
      } else {
        // Del mes: valores DIRECTOS del backend, aditivos entre obras. El % del
        // mes se pondera por COSTO (numCostoMes / costoCasa), igual que el acum.
        texto =
          metrica === 'pct'
            ? cur && cur.costoCasa > 0
              ? fmtPct(cur.numCostoMes / cur.costoCasa)
              : '—'
            : metrica === 'monto'
              ? fmtMonto(cur?.montoMes ?? null)
              : fmtM2(cur?.m2Mes ?? null);
      }
      // Si se colorea por estado, el fondo de estado reemplaza al heatmap.
      const claseHeatmap = bgEstado ? '' : colorAvance(pctAcum);
      return (
        <TableCell
          key={c.key}
          className={`text-center text-xs tabular-nums ${claseHeatmap} ${borderLeft}`}
          style={bgEstado ? { backgroundColor: bgEstado } : undefined}
          title={cur?.estado ?? undefined}
        >
          {texto}
        </TableCell>
      );
    });

  const tituloMetrica =
    metrica === 'pct' ? '% de Avance' : metrica === 'monto' ? 'Monto (₡ costo)' : 'm² construidos';
  const tituloModo = modo === 'acum' ? 'acumulado' : 'del mes';
  const modoSemanal = semanasSel.length > 0;

  // Valor numérico CRUDO de una celda según la métrica + modo activos (espejo de
  // renderCeldas, pero sin formatear). null = celda vacía / no aplica.
  const valorCrudo = (cur: Celda | undefined, esSemana: boolean): number | null => {
    if (!cur) return null;
    if (modo === 'acum') {
      if (esSemana) return null; // no hay acumulado por semana
      if (metrica === 'pct') return cur.pct;
      if (metrica === 'monto') return cur.montoAcum;
      return cur.m2Acum;
    }
    // Del mes
    if (metrica === 'pct') return cur.costoCasa > 0 ? cur.numCostoMes / cur.costoCasa : null;
    if (metrica === 'monto') return cur.montoMes;
    return cur.m2Mes;
  };

  const idxOrden = ordenCol ? hojas.findIndex((c) => c.key === ordenCol.key) : -1;
  const orden: OrdenColumna | null =
    ordenCol && idxOrden >= 0 ? { idx: idxOrden, dir: ordenCol.dir } : null;
  const alOrdenar = (idx: number) => {
    const sig = siguienteOrden(orden, idx);
    const col = sig ? hojas[sig.idx] : undefined;
    setOrdenCol(sig && col ? { key: col.key, dir: sig.dir } : null);
  };
  /** Valores de un nodo por columna (mismos números que se muestran, sin formato). */
  const valoresNodo = (n: { porColumna: Map<string, Celda> }) =>
    hojas.map((c) => valorCrudo(n.porColumna.get(c.key), c.semanaIso != null) || null);
  const proyectosOrd = ordenarHermanos(matriz.proyectos, orden, valoresNodo);
  const bloquesOrd = (p: NodoProyecto) => ordenarHermanos(p.bloques, orden, valoresNodo);
  const obrasOrd = (b: NodoBloque) => ordenarHermanos(b.obras, orden, valoresNodo);

  // Exporta la matriz COMPLETA a Excel con la métrica + modo activos.
  const exportar = () => {
    const filasExport: FilaExport[] = [];
    const valoresDe = (porColumna: Map<string, Celda>): Map<string, number | null> => {
      const m = new Map<string, number | null>();
      for (const c of hojas) m.set(c.key, valorCrudo(porColumna.get(c.key), c.semanaIso != null));
      return m;
    };
    // Estado de venta por celda de obra (para pintar como "Color por estado").
    const estadosDe = (porColumna: Map<string, Celda>): Map<string, string | null> => {
      const m = new Map<string, string | null>();
      for (const c of hojas) m.set(c.key, porColumna.get(c.key)?.estado ?? null);
      return m;
    };
    for (const p of proyectosOrd) {
      filasExport.push({ nivel: 0, etiqueta: p.proyecto, valores: valoresDe(p.porColumna) });
      for (const b of bloquesOrd(p)) {
        filasExport.push({ nivel: 1, etiqueta: b.bloque, valores: valoresDe(b.porColumna) });
        for (const o of obrasOrd(b)) {
          filasExport.push({
            nivel: 2,
            etiqueta: o.worksNo,
            sublabel: o.display && o.display !== o.worksNo ? o.display : null,
            valores: valoresDe(o.porColumna),
            estados: estadosDe(o.porColumna),
          });
        }
      }
    }
    filasExport.push({ nivel: 3, etiqueta: 'Gran Total', valores: valoresDe(matriz.total) });

    // Subtotales por estado: solo tienen sentido con monto/m² (no con %).
    if (metrica !== 'pct') {
      for (const est of ESTADOS_VENTA) {
        const porCol = matriz.totalPorEstado.get(est);
        if (!porCol) continue;
        filasExport.push({
          nivel: 4,
          etiqueta: est,
          valores: valoresDe(porCol),
          colorFila: colorArgbEstado(est, true),
        });
      }
    }

    const sufMetrica = metrica === 'pct' ? 'pct' : metrica === 'monto' ? 'costo' : 'm2';
    const sufModo = modo === 'acum' ? 'acum' : 'mes';
    exportarMatrizExcel({
      nombreArchivo: `avance-${sufMetrica}-${sufModo}`,
      tituloHoja: `Avance ${tituloMetrica} (${tituloModo})`,
      encabezadoJerarquia: 'Proyecto / Bloque / Obra',
      columnas: hojas,
      filas: filasExport,
      formato: metrica === 'pct' ? 'porcentaje' : metrica === 'monto' ? 'moneda' : 'numero',
    });
  };

  const filasRender: ReactElement[] = [];
  for (const proy of proyectosOrd) {
    const proyKey = `P|${proy.proyecto}`;
    const proyAbierto = expandidos.has(proyKey);
    filasRender.push(
      <TableRow key={proyKey} className="bg-emerald-100 font-semibold">
        <TableCell className="sticky left-0 z-10 bg-emerald-100 text-xs">
          <button
            type="button"
            onClick={() => toggle(proyKey)}
            className="flex items-center gap-1 hover:underline"
          >
            {proyAbierto ? (
              <CaretDown className="h-3 w-3" weight="bold" />
            ) : (
              <CaretRight className="h-3 w-3" weight="bold" />
            )}
            {proy.proyecto}
          </button>
        </TableCell>
        {renderCeldas(proy.porColumna)}
      </TableRow>,
    );
    if (!proyAbierto) continue;
    for (const blo of bloquesOrd(proy)) {
      const bloKey = `B|${blo.key}`;
      const bloAbierto = expandidos.has(bloKey);
      filasRender.push(
        <TableRow key={bloKey} className="bg-emerald-50 font-medium">
          <TableCell className="sticky left-0 z-10 bg-emerald-50 text-xs">
            <button
              type="button"
              onClick={() => toggle(bloKey)}
              className="flex items-center gap-1 pl-4 hover:underline"
            >
              {bloAbierto ? (
                <CaretDown className="h-3 w-3" weight="bold" />
              ) : (
                <CaretRight className="h-3 w-3" weight="bold" />
              )}
              {blo.bloque}
            </button>
          </TableCell>
          {renderCeldas(blo.porColumna)}
        </TableRow>,
      );
      if (!bloAbierto) continue;
      for (const obra of obrasOrd(blo)) {
        const oKey = `O|${obra.worksNo}`;
        const oAbierto = expandidos.has(oKey);
        filasRender.push(
          <TableRow key={oKey}>
            <TableCell className="sticky left-0 z-10 bg-background text-xs">
              <button
                type="button"
                onClick={() => toggle(oKey)}
                className="flex items-start gap-1 pl-9 text-left hover:underline"
              >
                {oAbierto ? (
                  <CaretDown className="mt-0.5 h-3 w-3 shrink-0" weight="bold" />
                ) : (
                  <CaretRight className="mt-0.5 h-3 w-3 shrink-0" weight="bold" />
                )}
                <span>
                  <span className="font-medium">{obra.worksNo}</span>
                  {obra.display && obra.display !== obra.worksNo && (
                    <span className="block text-[10px] text-muted-foreground truncate max-w-[200px]">
                      {obra.display}
                    </span>
                  )}
                </span>
              </button>
            </TableCell>
            {renderCeldas(obra.porColumna, true)}
          </TableRow>,
        );
        // Drill a partidas: pct y monto (m² no aplica a partida → cae a monto).
        if (oAbierto) {
          filasRender.push(
            <FilasPartidasAvance
              key={`PT|${obra.worksNo}`}
              worksNo={obra.worksNo}
              cols={hojas}
              metrica={metrica === 'm2' ? 'monto' : metrica}
              modo={modo}
            />,
          );
        }
      }
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Avance por Mes</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {tituloMetrica} ({tituloModo}) por obra. Todo ponderado por <strong>costo directo</strong>{' '}
          (= Power BI): % avance = Σ(avance×costo)/Σcosto; monto = costo teórico construido; m² =
          avance × área. El color refleja siempre el % acumulado. Agrupado por{' '}
          <strong>Proyecto → Bloque → Obra</strong>; click en el ▾ para drill a partidas. Usá el
          selector <strong>Semanas</strong> para ver el desglose por semana ISO.{' '}
          <em>La producción contable real (venta) está en la pantalla Producción Mensual.</em>
        </p>
        {modoSemanal && modo === 'acum' && (
          <p className="mt-1 text-xs text-amber-700">
            El desglose semanal solo aplica al modo <strong>Del mes</strong> (no hay acumulado por
            semana). Cambiá a <strong>Del mes</strong> para ver los valores semanales.
          </p>
        )}
        {totalPorEstadoAbierto && metrica === 'pct' && (
          <p className="mt-1 text-xs text-amber-700">
            El desglose del total por estado solo aplica a <strong>₡ Costo</strong> y{' '}
            <strong>m²</strong> (sumar % por estado no tiene sentido). Cambiá la métrica para ver
            los subtotales.
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
          <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
            {(['pct', 'monto', 'm2'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMetrica(m)}
                className={`px-2.5 py-1 ${metrica === m ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
              >
                {m === 'pct' ? '% Avance' : m === 'monto' ? '₡ Costo' : 'm²'}
              </button>
            ))}
          </div>
          <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
            <button
              type="button"
              onClick={() => setModo('acum')}
              className={`px-2.5 py-1 ${modo === 'acum' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              Acumulado
            </button>
            <button
              type="button"
              onClick={() => setModo('mes')}
              className={`px-2.5 py-1 ${modo === 'mes' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              Del mes
            </button>
          </div>
          {/* Toggle Mes / Semana */}
          <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
            <button
              type="button"
              onClick={() => setVista('mes')}
              className={`px-2.5 py-1 ${vista === 'mes' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              Mes
            </button>
            <button
              type="button"
              onClick={() => setVista('semana')}
              className={`px-2.5 py-1 ${vista === 'semana' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              Semana
            </button>
          </div>
          {vista === 'semana' && (
            <FiltroPeriodosSemanal
              disponibles={periodosSem?.data ?? []}
              seleccion={semanasSelRaw}
              onChange={setSemanasSelRaw}
            />
          )}
          {vista === 'semana' && semanasSelRaw.length === 0 && (
            <span className="text-amber-700">Marcá semanas en el selector</span>
          )}
          {/* Checkbox: colorear por estado de venta */}
          <label className="flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={colorearEstado}
              onChange={(e) => setColorearEstado(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Color estado
          </label>
          {colorearEstado && (
            <span className="flex items-center gap-2">
              {ESTADOS_VENTA.map((est) => (
                <span key={est} className="flex items-center gap-1">
                  <span
                    className="inline-block h-2.5 w-3 rounded-sm"
                    style={{ backgroundColor: colorEstado(est) }}
                  />
                  {est}
                </span>
              ))}
            </span>
          )}
          <span className="text-zinc-300">|</span>
          <button
            type="button"
            onClick={() => setExpandidos(new Set(todasLasKeys))}
            className="text-blue-600 hover:underline"
          >
            Expandir todo
          </button>
          <span className="text-zinc-300">|</span>
          <button
            type="button"
            onClick={() => setExpandidos(new Set())}
            className="text-blue-600 hover:underline"
          >
            Colapsar todo
          </button>
          <span className="text-zinc-300">|</span>
          <button
            type="button"
            onClick={exportar}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50"
          >
            <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
            Exportar a Excel
          </button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table wrapperClassName="max-h-[calc(100vh-220px)]">
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 top-0 z-30 bg-background min-w-[220px]">
                  Proyecto / Bloque / Obra
                </TableHead>
                {matriz.arbol.anios.map((anio) => {
                  const cnt = hojas.filter((c) => c.anio === anio).length;
                  return (
                    <TableHead
                      key={anio}
                      colSpan={cnt}
                      className="sticky top-0 z-20 bg-background text-center border-l border-zinc-200 text-xs"
                    >
                      {anio}
                    </TableHead>
                  );
                })}
              </TableRow>
              <TableRow>
                <TableHead className="sticky left-0 top-[33px] z-30 bg-background" />
                {matriz.arbol.meses.map((m) => {
                  // El mes se desglosa en semanas cuando hay semanas marcadas en
                  // el selector (que pueblan m.semanas vía el dataset semanal).
                  const colSpan = m.semanas.length > 0 ? m.semanas.length : 1;
                  return (
                    <TableHead
                      key={m.mkey}
                      colSpan={colSpan}
                      className="sticky top-[33px] z-20 bg-background text-center text-xs min-w-[60px] border-l border-zinc-200"
                    >
                      {m.semanas.length === 0 ? (
                        <TituloOrdenable
                          titulo={NOMBRES_MES[m.mes - 1] ?? ''}
                          idx={hojas.findIndex(
                            (h) => h.semanaIso == null && h.anio === m.anio && h.mes === m.mes,
                          )}
                          orden={orden}
                          onOrdenar={alOrdenar}
                        />
                      ) : (
                        NOMBRES_MES[m.mes - 1]
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
              <TableRow>
                <TableHead className="sticky left-0 top-[66px] z-30 bg-background" />
                {hojas.map((c, idx) => (
                  <TableHead
                    key={c.key}
                    className={`sticky top-[66px] z-20 bg-background text-center text-[10px] text-muted-foreground min-w-[60px] ${
                      iniciaAnio(idx) ? 'border-l border-zinc-200' : ''
                    }`}
                  >
                    {c.semanaIso != null ? (
                      <TituloOrdenable
                        titulo={etiquetaSemana(c.semanaIso)}
                        idx={idx}
                        orden={orden}
                        onOrdenar={alOrdenar}
                      />
                    ) : (
                      ''
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filasRender}
              <TableRow className="border-t-2 border-zinc-300 bg-zinc-100 font-bold hover:bg-zinc-100">
                <TableCell className="sticky left-0 z-10 bg-zinc-100 text-xs">
                  <button
                    type="button"
                    onClick={() => setTotalPorEstadoAbierto((v) => !v)}
                    className="flex items-center gap-1 hover:underline"
                    title="Desglosar por estado de venta (solo ₡ y m²)"
                  >
                    {totalPorEstadoAbierto ? (
                      <CaretDown className="h-3 w-3" weight="bold" />
                    ) : (
                      <CaretRight className="h-3 w-3" weight="bold" />
                    )}
                    Gran Total
                  </button>
                </TableCell>
                {renderCeldas(matriz.total)}
              </TableRow>
              {totalPorEstadoAbierto &&
                ESTADOS_VENTA.map((est) => {
                  const porCol = matriz.totalPorEstado.get(est);
                  const bg = colorFondoEstado(est);
                  return (
                    <TableRow key={`TE|${est}`} className="bg-zinc-50 font-medium">
                      <TableCell className="sticky left-0 z-10 bg-zinc-50 text-xs">
                        <span className="flex items-center gap-1.5 pl-4">
                          <span
                            className="inline-block h-2.5 w-3 rounded-sm"
                            style={{ backgroundColor: colorEstado(est) }}
                          />
                          {est}
                        </span>
                      </TableCell>
                      {hojas.map((c, idx) => {
                        const esSemana = c.semanaIso != null;
                        const borderLeft = iniciaAnio(idx) ? 'border-l border-zinc-200' : '';
                        // % no se suma por estado; solo monto/m² tienen sentido.
                        const v =
                          metrica === 'pct' ? null : valorCrudo(porCol?.get(c.key), esSemana);
                        const texto =
                          metrica === 'pct' ? '—' : metrica === 'monto' ? fmtMonto(v) : fmtM2(v);
                        return (
                          <TableCell
                            key={c.key}
                            className={`text-center text-xs tabular-nums ${borderLeft}`}
                            style={{ backgroundColor: bg }}
                          >
                            {texto}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
